import { DriverRankingsService } from '@/backend/market'
import { PaginateEntity } from '@/shared/entities/PaginateEntity'
import type { DriverRankingDetailEntity } from '../../domain/entities/DriverRankingDetailEntity'
import type { DriverRankingEntity } from '../../domain/entities/DriverRankingEntity'
import type { DriverRankingFilterEntity } from '../../domain/entities/DriverRankingFilterEntity'
import type { DriverRankingManualRunEntity } from '../../domain/entities/DriverRankingManualRunEntity'
import type { DriverRankingPeriodEntity } from '../../domain/entities/DriverRankingPeriodEntity'
import type { DriverRankingPeriodFilterEntity } from '../../domain/entities/DriverRankingPeriodFilterEntity'
import { DriverRankingRepository } from '../../domain/repositories/DriverRankingRepository'
import { DriverRankingDetailModel } from '../models/DriverRankingDetailModel'
import { DriverRankingFilterModel } from '../models/DriverRankingFilterModel'
import { DriverRankingModel } from '../models/DriverRankingModel'
import { DriverRankingPeriodFilterModel } from '../models/DriverRankingPeriodFilterModel'
import { DriverRankingPeriodModel } from '../models/DriverRankingPeriodModel'

export class DriverRankingRepositoryImpl implements DriverRankingRepository {
  async getDriverRankings(
    filterData: DriverRankingFilterEntity
  ): Promise<PaginateEntity<DriverRankingEntity[]> | { error: true }> {
    const filterModel = new DriverRankingFilterModel(filterData)

    try {
      const response = await DriverRankingsService.getDriverRankingsAdmin(filterModel)
      const items = response.items?.map((item) => new DriverRankingModel(item))
      return new PaginateEntity<DriverRankingEntity[]>(
        {
          count: response.count,
          page: response.page ? response.page - 1 : 0,
          limit: response.limit,
          items
        },
        items as unknown as DriverRankingEntity[]
      )
    } catch {
      return { error: true }
    }
  }

  async getDriverRanking(id: string): Promise<DriverRankingDetailEntity | { error: true }> {
    try {
      const response = await DriverRankingsService.getDriverRankingByIdAdmin({ id })
      return new DriverRankingDetailModel(response)
    } catch {
      return { error: true }
    }
  }

  async getDriverRankingPeriods(
    filterData: DriverRankingPeriodFilterEntity
  ): Promise<PaginateEntity<DriverRankingPeriodEntity[]> | { error: true }> {
    const filterModel = new DriverRankingPeriodFilterModel(filterData)

    try {
      const response = await DriverRankingsService.getDriverRankingPeriodsAdmin(filterModel)
      const items = response.items?.map((item) => new DriverRankingPeriodModel(item))
      return new PaginateEntity<DriverRankingPeriodEntity[]>(
        {
          count: response.count,
          page: response.page ? response.page - 1 : 0,
          limit: response.limit,
          items
        },
        items as unknown as DriverRankingPeriodEntity[]
      )
    } catch {
      return { error: true }
    }
  }

  async validateRunDriverRankingsManually(data: DriverRankingManualRunEntity) {
    try {
      return await DriverRankingsService.validateRunDriverRankingsManuallyAdmin({
        requestBody: {
          rankingPeriodIds: data.rankingPeriods?.map((item) => item.id!).filter(Boolean) ?? []
        }
      })
    } catch {
      return { error: true } as const
    }
  }

  async runDriverRankingsManually(data: DriverRankingManualRunEntity) {
    try {
      await DriverRankingsService.runDriverRankingsManuallyAdmin({
        requestBody: {
          rankingPeriodIds: data.rankingPeriods?.map((item) => item.id!).filter(Boolean) ?? []
        }
      })
      return true as const
    } catch {
      return { error: true } as const
    }
  }
}
